import React, { useState, useEffect, useCallback, useContext } from 'react';
import RoomContext from '../RoomContext';
import { useRoomApi } from '../hooks/useRoomApi';
import { roleLabel } from './deviceRoles';
import './DeviceStatus.css';

// Approximate "how long ago" in Spanish. The backend's timestamp resets on its restart, so this is
// deliberately coarse (minutes/hours/days) rather than to-the-second.
function timeAgo(iso) {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const mins = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (mins < 1) return 'hace instantes';
  if (mins < 60) return `hace ${mins} min`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `hace ${hours} h`;
  const days = Math.round(hours / 24);
  return `hace ${days} d`;
}

function deviceState(d) {
  if (d.online === true) return 'online';
  if (d.online === false) return 'offline';
  return 'unknown';
}

const STATE_LABEL = { online: 'En línea', offline: 'Desconectado', unknown: 'Sin datos' };
// When a "since" moment is known, it stands in for the plain state label; for online it's the connect
// time, for offline the drop. An unknown device has no such moment.
const SINCE_LABEL = { online: 'En línea desde', offline: 'Desconectado desde' };

// Small header control: a signal button that opens a popover listing every device in the room with its
// online/offline state. A red badge on the button draws attention whenever any device is offline.
const DeviceStatus = () => {
  const { tenant, room } = useContext(RoomContext);
  const api = useRoomApi(tenant, room);
  const [devices, setDevices] = useState(null);
  const [open, setOpen] = useState(false);

  const refresh = useCallback(async () => {
    try {
      setDevices(await api.getDevices());
    } catch (e) {
      // Header widget stays quiet on transient errors; the main view surfaces connectivity problems.
    }
  }, [api]);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 15000);
    return () => clearInterval(id);
  }, [refresh]);

  const anyOffline = (devices ?? []).some(d => d.online === false);

  return (
    <div className="device-status">
      <button
        type="button"
        className="btn btn-sm btn-outline-secondary position-relative"
        onClick={() => setOpen(o => !o)}
        aria-label="Estado de los dispositivos"
        title="Estado de los dispositivos"
      >
        📶
        {anyOffline && <span className="device-status-badge" />}
      </button>

      {open && (
        <>
          <div className="device-status-backdrop" onClick={() => setOpen(false)} />
          <div className="device-status-panel">
            <div className="small fw-semibold mb-2">Dispositivos</div>
            {devices == null && <div className="small text-muted">Cargando…</div>}
            {devices != null && devices.length === 0 && (
              <div className="small text-muted">No hay dispositivos asignados.</div>
            )}
            {(devices ?? []).map((d) => {
              const state = deviceState(d);
              const ago = state !== 'unknown' && d.statusChangedAt ? timeAgo(d.statusChangedAt) : null;
              return (
                <div key={d.mac} className="d-flex align-items-start gap-2 mb-2">
                  <span className={`device-status-dot ${state} mt-1`} />
                  <div className="flex-grow-1">
                    <div className="small fw-semibold">{roleLabel(d.role)}</div>
                    <div className="small text-muted">
                      {ago ? `${SINCE_LABEL[state]} ${ago}` : STATE_LABEL[state]}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default DeviceStatus;
